import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaMagic } from "react-icons/fa";
import {
  AwardLetterDto,
  AwardLetterPatchDto,
  AwardLetterStatusDto,
  AwardLetterTypeDto,
  useApproveAwardLetterMutation,
  useReopenAwardLetterMutation,
  useUpdateAwardLetterMutation,
} from "../../../../autogen/bff-api";
import {
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { useSub } from "../../../../common/subscription/useSub";
import { rejectionIntro, statutoryClauses, StatutoryClauseContext, winnerIntro } from "./awardLetterTemplates";
import { AwardLetterPreview } from "./AwardLetterPreview";
import { AwardLetterDraft, AwardLetterDraftInput, useAwardLetterDraft } from "./useAwardLetterDraft";
import { useProcurementTerms } from "../../../../common/terminology/useProcurementTerms";

interface Props {
  eventId: string;
  letter: AwardLetterDto;
  aiInput: AwardLetterDraftInput;
  winnerName: string;
  eventTitle: string;
  senderName?: string | null;
  signerName?: string | null;
  clauseContext: StatutoryClauseContext;
  isOpen: boolean;
  onClose: () => void;
}

const AUTOSAVE_DEBOUNCE_MS = 300;

export const AwardLetterEditor = (props: Props) => {
  const { eventId, letter } = props;
  const [update] = useUpdateAwardLetterMutation();
  const editorErrorConfig = useApiErrorEditorConfig();

  useCreateEditor<AwardLetterDto, AwardLetterPatchDto>(letter.id, {
    initialState: letter,
    save: (patch) => update({ eventId, letterId: letter.id, awardLetterPatchDto: patch }).unwrap(),
    ...editorErrorConfig,
  });

  return <AwardLetterEditorBody {...props} />;
};

const AwardLetterEditorBody = ({
  eventId,
  letter,
  aiInput,
  winnerName,
  eventTitle,
  senderName,
  signerName,
  clauseContext,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation();
  const clauses = statutoryClauses(clauseContext);
  const terms = useProcurementTerms();
  const toast = useToast();
  const apiError = useApiError();
  const sub = useSub();
  const preview = useDisclosure();
  const [approve, { isLoading: isApproving }] = useApproveAwardLetterMutation();
  const [reopen, { isLoading: isReopening }] = useReopenAwardLetterMutation();
  const { state, updateField, entityStatus } = useEditor<AwardLetterDto, AwardLetterPatchDto>(letter.id);

  const isRejection = state.type === AwardLetterTypeDto.Rejection;
  const isApproved = state.status === AwardLetterStatusDto.Approved;
  const isSent = state.status === AwardLetterStatusDto.Sent;
  const isLocked = isApproved || isSent;
  const supplierName = state.supplierName || t("Unknown") || "";
  const intro = isRejection
    ? rejectionIntro(eventTitle, winnerName, senderName)
    : winnerIntro(eventTitle, senderName);
  const justifications = useMemo(
    () => state.content.criterionJustifications ?? {},
    [state.content.criterionJustifications],
  );
  const freeText = state.content.freeText ?? "";

  const recipientScoreFor = (criterionId: string) =>
    aiInput.recipient.responses.find((r) => r.criterionId === criterionId)?.points ?? null;
  const winnerScoreFor = (criterionId: string) =>
    aiInput.winner.responses.find((r) => r.criterionId === criterionId)?.points ?? null;

  // Scores in {criterionId → {supplierPoints, winnerPoints}} shape for the preview component.
  const previewScores = aiInput.criteria.reduce<
    Record<string, { supplierPoints: number | null; winnerPoints: number | null }>
  >((acc, c) => {
    acc[c.id] = { supplierPoints: recipientScoreFor(c.id), winnerPoints: winnerScoreFor(c.id) };
    return acc;
  }, {});

  // The BFF replaces criterionJustifications wholesale (Patch<Map>), so always send the full map.
  // The coordinator's optimistic state recurses into the map per key, which lines up with the
  // server's whole-map replace because we're sending the whole intended state.
  const setJustification = (criterionId: string, value: string) => {
    updateField(
      { content: { criterionJustifications: { ...justifications, [criterionId]: value } } },
      { debounceMs: AUTOSAVE_DEBOUNCE_MS },
    );
  };
  const setFreeText = (value: string) => {
    updateField({ content: { freeText: value } }, { debounceMs: AUTOSAVE_DEBOUNCE_MS });
  };

  const onDraft = useCallback(
    (d: AwardLetterDraft) => {
      updateField({
        content: {
          criterionJustifications: { ...justifications, ...d.criterionJustifications },
          ...(d.freeText ? { freeText: d.freeText } : {}),
        },
      });
    },
    [justifications, updateField],
  );

  // Atomic save + approve: we send the latest content as the approve body, so the BFF applies
  // the content patch and the approval in one transaction. This eliminates any race between
  // a still-pending autosave and the approve call.
  const onApprove = async () => {
    try {
      await approve({
        eventId,
        letterId: letter.id,
        awardLetterPatchDto: {
          content: {
            criterionJustifications: justifications,
            freeText: freeText.trim() || null,
          },
        },
      }).unwrap();
      toast({ title: t("Letter approved"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const onReopen = async () => {
    try {
      await reopen({ eventId, letterId: letter.id }).unwrap();
      toast({ title: t("Letter re-opened for edit"), status: "info", position: "top-right" });
    } catch (e) {
      apiError.trigger(e);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="smBackgroundSecondary">
          <ModalHeader>
            <HStack spacing="3" flexWrap="wrap">
              <Text>
                {isRejection ? t("Rejection letter") : t("Winner letter")}: {supplierName}
              </Text>
              <Badge colorScheme={statusColorScheme(state.status, isRejection)}>
                {statusLabel(state.status, isRejection, t)}
              </Badge>
              <SaveIndicator status={entityStatus} />
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="5">
              <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p="3">
                <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="1">
                  {t("Template (intro)")}
                </Text>
                <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                  {intro}
                </Text>
              </Box>

              {!isLocked && sub.hasAiAccess && (
                <AiDraftSection
                  disabled={isApproving || isReopening}
                  input={aiInput}
                  onDraft={onDraft}
                  justifications={justifications}
                  freeText={freeText}
                  label={
                    isRejection
                      ? t("Suggest justifications and closing text") || ""
                      : t("Suggest a congratulatory note") || ""
                  }
                />
              )}

              {isRejection && (
                <Box>
                  <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="2">
                    {t("Justification per criterion")}
                  </Text>
                  <Stack spacing="3">
                    {aiInput.criteria.map((c) => (
                      <FormControl key={c.id}>
                        <FormLabel fontSize="sm" mb="1">
                          {c.name} ({c.weightPercent}%) ·{" "}
                          <Text as="span" color="smMuted">
                            {t("Your score")} {recipientScoreFor(c.id) ?? "–"} / {t("winner")}{" "}
                            {winnerScoreFor(c.id) ?? "–"}
                          </Text>
                        </FormLabel>
                        <Textarea
                          value={justifications[c.id] ?? ""}
                          onChange={(e) => setJustification(c.id, e.target.value)}
                          isDisabled={isLocked}
                          bg="smBackground"
                          rows={3}
                          placeholder={t("Why the winner scored better on this criterion…") ?? ""}
                        />
                      </FormControl>
                    ))}
                  </Stack>
                </Box>
              )}

              <FormControl>
                <FormLabel fontSize="sm">{t("Additional text (optional)")}</FormLabel>
                <Textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  isDisabled={isLocked}
                  bg="smBackground"
                  rows={3}
                />
              </FormControl>

              {(clauses.standstill || clauses.complaint) && (
                <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p="3">
                  <Text fontSize="xs" fontWeight="medium" color="smMuted" textTransform="uppercase" mb="1">
                    {t("Template (standstill and complaint)")}
                  </Text>
                  {clauses.standstill && (
                    <Text fontSize="sm" color="smMuted" whiteSpace="pre-wrap">
                      {clauses.standstill}
                    </Text>
                  )}
                  {clauses.complaint && (
                    <Text fontSize="sm" color="smMuted" whiteSpace="pre-wrap" mt="2">
                      {clauses.complaint}
                    </Text>
                  )}
                </Box>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter gap="2">
            <Button variant="ghost" onClick={preview.onOpen}>
              {t("Preview")}
            </Button>
            {isSent && <Button onClick={onClose}>{t("Close")}</Button>}
            {isApproved && (
              <Button variant="outline" onClick={onReopen} isLoading={isReopening}>
                {t("Re-open for edit")}
              </Button>
            )}
            {!isLocked && (
              <Button colorScheme="teal" onClick={onApprove} isLoading={isApproving}>
                {t("Mark as approved")}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={preview.isOpen} onClose={preview.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Preview of award letter")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <AwardLetterPreview
              type={state.type}
              supplierName={supplierName}
              winnerName={winnerName}
              eventTitle={eventTitle}
              senderName={senderName}
              signerName={signerName}
              criterionJustifications={justifications}
              freeText={freeText}
              criteria={aiInput.criteria}
              scores={previewScores}
              sentAt={state.sentAt}
              clauseContext={clauseContext}
              scoreHeading={terms.scorePerAwardCriterion}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const statusColorScheme = (status: AwardLetterStatusDto, isRejection: boolean): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return "green";
    case AwardLetterStatusDto.Approved:
      return "teal";
    default:
      return isRejection ? "gray" : "teal";
  }
};

const statusLabel = (
  status: AwardLetterStatusDto,
  isRejection: boolean,
  t: (k: string) => string,
): string => {
  switch (status) {
    case AwardLetterStatusDto.Sent:
      return t("Sent");
    case AwardLetterStatusDto.Approved:
      return t("Approved");
    default:
      return isRejection ? t("Not selected") : t("Winner");
  }
};

const SaveIndicator = ({ status }: { status: "idle" | "pending" | "saving" | "saved" | "error" }) => {
  const { t } = useTranslation();
  if (status === "saving" || status === "pending") {
    return <Text fontSize="xs" color="smMuted">{t("Saving…")}</Text>;
  }
  if (status === "saved") {
    return <Text fontSize="xs" color="smMuted">{t("Saved")}</Text>;
  }
  if (status === "error") {
    return <Text fontSize="xs" color="smError">{t("Save failed")}</Text>;
  }
  return null;
};

const AiDraftSection = ({
  input,
  onDraft,
  disabled,
  label,
  justifications,
  freeText,
}: {
  input: AwardLetterDraftInput;
  onDraft: (d: AwardLetterDraft) => void;
  disabled?: boolean;
  label: string;
  justifications: Record<string, string>;
  freeText: string;
}) => {
  return (
    <AiDraftButton
      input={input}
      onDraft={onDraft}
      disabled={disabled}
      label={label}
      justifications={justifications}
      freeText={freeText}
    />
  );
};

const AiDraftButton = ({
  input,
  onDraft,
  disabled,
  label,
  justifications,
  freeText,
}: {
  input: AwardLetterDraftInput;
  onDraft: (d: AwardLetterDraft) => void;
  disabled?: boolean;
  label: string;
  justifications: Record<string, string>;
  freeText: string;
}) => {
  const { t } = useTranslation();
  const { draft, isLoading, hasError, failedCriterionIds } = useAwardLetterDraft();

  // Re-clicking only redrafts criteria that are still empty, so a flaky criterion can be retried
  // without re-rolling the ones that already succeeded. When nothing is empty, redraft everything.
  const onClick = async () => {
    const empty = input.criteria.filter((c) => !(justifications[c.id] ?? "").trim());
    const criteria = empty.length > 0 ? empty : input.criteria;
    const result = await draft({ ...input, criteria }, { includeClosing: !freeText.trim() });
    if (result) onDraft(result);
  };

  return (
    <Box>
      <Button
        size="sm"
        leftIcon={<FaMagic />}
        variant="outline"
        colorScheme="purple"
        onClick={onClick}
        isLoading={isLoading}
        isDisabled={disabled}
      >
        {label}
      </Button>
      {hasError && (
        <Text fontSize="sm" color="smError" mt="1">
          {t("Could not generate a suggestion. Please try again.")}
        </Text>
      )}
      {!hasError && failedCriterionIds.length > 0 && (
        <Text fontSize="sm" color="smWarning" mt="1">
          {t("Could not generate {{failed}} of {{total}} justifications. Click again to retry the rest.", {
            failed: failedCriterionIds.length,
            total: input.criteria.length,
          })}
        </Text>
      )}
    </Box>
  );
};
